import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/nav';
import SEO from '../components/SEO';

// ── Inject keyframes once ─────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('pb-guide-styles')) return;
  const s = document.createElement('style');
  s.id = 'pb-guide-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500&display=swap');
    
    .pb-guide-root * { box-sizing: border-box; margin: 0; padding: 0; }
    
    @keyframes pb-blink { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes pb-float {
      0%   { transform: translateY(0px) translateX(0px); opacity: 0; }
      15%  { opacity: 0.6; }
      85%  { opacity: 0.4; }
      100% { transform: translateY(-140px) translateX(20px); opacity: 0; }
    }
    @keyframes pb-pulse-ring {
      0%   { transform: scale(0.8); opacity: 0.8; }
      100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes pb-slide-in-up {
      from { opacity: 0; transform: translateY(30px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pb-progress {
      from { width: 0%; }
      to   { width: 100%; }
    }
    @keyframes pb-shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes pb-typewriter-cursor {
      0%,100% { opacity: 1; }
      50%      { opacity: 0; }
    }
    
    .pb-cursor {
      display: inline-block;
      width: 3px;
      height: 0.85em;
      background: #00c9a7;
      vertical-align: text-bottom;
      margin-left: 2px;
      animation: pb-blink 0.75s step-end infinite;
      border-radius: 1px;
    }
    
    .pb-particle {
      position: absolute;
      border-radius: 50%;
      animation: pb-float linear infinite;
      pointer-events: none;
    }
    
    .pb-slide-enter {
      animation: pb-slide-in-up 0.7s cubic-bezier(0.16,1,0.3,1) both;
    }
    
    .pb-progress-bar {
      animation: pb-progress linear both;
    }
    
    .pb-tag-shimmer {
      background: linear-gradient(
        90deg,
        rgba(0,201,167,0.15) 0%,
        rgba(0,201,167,0.35) 50%,
        rgba(0,201,167,0.15) 100%
      );
      background-size: 200% auto;
      animation: pb-shimmer 3s linear infinite;
    }
    
    .pb-btn-primary:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(0,201,167,0.35); }
    .pb-btn-primary:active { transform: translateY(-1px); }
    .pb-btn-ghost:hover { border-color: #00c9a7; color: #00c9a7; }
    
    .pb-dot-active { width: 28px !important; border-radius: 3px !important; background: #00c9a7 !important; }
    .pb-ctrl:hover { background: rgba(0,201,167,0.12); color: #00c9a7; border-color: rgba(0,201,167,0.4); }
    
    .pb-pcard:hover { transform: translateY(-4px); border-color: rgba(0,201,167,0.3); }
    .pb-stage-box:hover { transform: translateY(-2px); }
  `;
  document.head.appendChild(s);
};

// ── Slide data ────────────────────────────────────────────────────────────────
const SLIDES = [
  {
    id: 'hero',
    kicker: 'Powered by Pilomation AI',
    headline: 'Every great film\nbegins with\none idea.',
    body: 'From the first spark of imagination to the final frame on screen — ProducerBuddy is the only platform built for everyone in the film world. Producers. Screenwriters. Crew. Enthusiasts. All connected. All thriving.',
    bg: 'radial-gradient(ellipse at 30% 60%, rgba(0,201,167,0.08) 0%, transparent 55%), radial-gradient(ellipse at 75% 25%, rgba(0,145,234,0.06) 0%, transparent 45%)',
    accentColor: '#00c9a7',
    type: 'tags',
    tags: [
      { label: 'AI Script Generation', color: 'teal' },
      { label: 'Smart Casting', color: 'blue' },
      { label: 'Location Intelligence', color: 'amber' },
      { label: 'Budget Forecasting', color: 'teal' },
      { label: 'Post Production Suite', color: 'blue' },
      { label: '3-Stage Workflow', color: 'amber' },
    ],
  },
  {
    id: 'preproduction',
    kicker: 'Pre-Production · Stage 01',
    headline: 'Your idea deserves\na screenplay.\nNot someday. Now.',
    body: 'You have a feeling. A story that needs to be told. Our AI listens — not just to words, but to the emotion behind them. It transforms raw thoughts into structured, market-ready screenplays in minutes — complete with scene breakdowns, character arcs, and shot lists.',
    bg: 'radial-gradient(ellipse at 50% 0%, rgba(0,201,167,0.1) 0%, transparent 60%)',
    accentColor: '#00c9a7',
    type: 'features',
    features: [
      'AI script generation from a single idea or prompt',
      'Genre detection & narrative structure guidance',
      'Automatic scene breakdown & shot lists',
      'Budget estimation before a camera rolls',
      'Location scouting powered by visual AI',
      'Smart casting suggestions from your brief',
    ],
  },
  {
    id: 'struggle',
    kicker: 'The Real World of Filmmaking',
    headline: 'The grind is real.\nSo is our\nsolution.',
    body: 'Endless audition rounds. Location hunting across cities. Budget spreadsheets that never balance. Casting calls with zero response. The film industry is brutal — unless you have the right platform behind you.',
    bg: 'radial-gradient(ellipse at 80% 60%, rgba(255,167,38,0.07) 0%, transparent 50%)',
    accentColor: '#ffb300',
    type: 'personas',
    personas: [
      {
        icon: '🎬',
        title: 'The Producer',
        text: 'Manages scripts, crew, budgets and timelines without switching 7 different tools. Everything lives in ProducerBuddy.',
      },
      {
        icon: '🎭',
        title: 'The Film Enthusiast',
        text: 'Discovers open roles, applies directly to productions, tracks auditions — no middlemen, no missed opportunities.',
      },
      {
        icon: '📣',
        title: 'The Marketing Mind',
        text: 'Connects with productions, showcases skills, collaborates on campaigns — all from one powerful profile.',
      },
    ],
  },
  {
    id: 'production',
    kicker: 'Production · Stage 02',
    headline: 'When cameras roll,\nchaos doesn\'t\nhave to.',
    body: "ProducerBuddy's AI coordinates your entire production — from daily call sheets to real-time budget tracking — so your team can focus entirely on making something extraordinary.",
    bg: 'radial-gradient(ellipse at 20% 40%, rgba(0,145,234,0.09) 0%, transparent 60%)',
    accentColor: '#4fc3f7',
    type: 'stages',
  },
  {
    id: 'ai',
    kicker: 'AI at its core',
    headline: 'This is what AI\nbuilt for film\ntruly looks like.',
    body: '',
    bg: 'radial-gradient(ellipse at 50% 50%, rgba(0,201,167,0.07) 0%, transparent 65%)',
    accentColor: '#00c9a7',
    type: 'quote',
    quote: '"I typed three sentences about a road trip gone wrong. ProducerBuddy\'s AI turned it into a 90-page screenplay, a location shortlist, and a full casting brief — in under 20 minutes."',
    quoteAuthor: '— Independent Screenwriter, ProducerBuddy user',
  },
  {
    id: 'cta',
    kicker: 'Your story starts here',
    headline: 'Your next film\nis one click\naway.',
    body: 'Whether you\'re a first-time writer with a dream, a production house seeking talent, or a marketer ready to collaborate — ProducerBuddy is built for you. The film industry\'s future is connected.',
    bg: 'radial-gradient(ellipse at 50% 50%, rgba(0,201,167,0.12) 0%, transparent 70%)',
    accentColor: '#00c9a7',
    type: 'cta',
  },
];

const DURATION = 6500;
const TYPE_SPEED = 38;

// ── Tag colors ────────────────────────────────────────────────────────────────
const TAG_STYLES = {
  teal:  { bg: 'rgba(0,201,167,0.12)', color: '#00c9a7', border: 'rgba(0,201,167,0.3)' },
  blue:  { bg: 'rgba(0,145,234,0.12)', color: '#4fc3f7', border: 'rgba(0,145,234,0.3)' },
  amber: { bg: 'rgba(255,167,38,0.12)', color: '#ffb300', border: 'rgba(255,167,38,0.3)' },
};

// ── Stage data ────────────────────────────────────────────────────────────────
const STAGES = [
  { label: 'Pre', title: 'Write & Plan', desc: 'Script · Budget · Cast · Location', color: '#00c9a7', bg: 'rgba(0,201,167,0.1)', border: 'rgba(0,201,167,0.25)' },
  { label: 'Production', title: 'Shoot & Direct', desc: 'Schedule · Crew · Daily Logs', color: '#4fc3f7', bg: 'rgba(0,145,234,0.1)', border: 'rgba(0,145,234,0.25)' },
  { label: 'Post', title: 'Edit & Release', desc: 'VFX · Sound · Distribution', color: '#ffb300', bg: 'rgba(255,167,38,0.1)', border: 'rgba(255,167,38,0.25)' },
];

const STATS = [
  { value: '73%', label: 'faster pre-production' },
  { value: '10×', label: 'more casting reach' },
  { value: '∞', label: 'stories to tell' },
];

// ── Typewriter hook ───────────────────────────────────────────────────────────
function useTypewriter(text, active) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    clearInterval(timerRef.current);
    setDisplayed('');
    setDone(false);
    if (!active) return;

    const chars = text.split('');
    let i = 0;
    timerRef.current = setInterval(() => {
      if (i >= chars.length) {
        clearInterval(timerRef.current);
        setDone(true);
        return;
      }
      setDisplayed(prev => prev + chars[i]);
      i++;
    }, TYPE_SPEED);

    return () => clearInterval(timerRef.current);
  }, [text, active]);

  return { displayed, done };
}

// ── Headline renderer (handles \n) ────────────────────────────────────────────
function Headline({ text, active, accentColor }) {
  const { displayed, done } = useTypewriter(text, active);
  const lines = displayed.split('\n');

  return (
    <div style={{
      fontFamily: "'Playfair Display', serif",
      fontSize: 'clamp(32px, 5.5vw, 58px)',
      fontWeight: 900,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
      color: '#f1f5f9',
      minHeight: '3.5em',
      marginBottom: 28,
    }}>
      {lines.map((line, i) => (
        <span key={i} style={{ display: 'block' }}>{line}</span>
      ))}
      {!done && <span className="pb-cursor" style={{ background: accentColor }} />}
    </div>
  );
}

// ── Particle background ───────────────────────────────────────────────────────
function Particles({ color }) {
  const particles = useRef(
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      size: Math.random() * 4 + 2,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: Math.random() * 9 + 7,
      delay: Math.random() * 6,
      isBlue: Math.random() > 0.5,
    }))
  ).current;

  return (
    <>
      {particles.map(p => (
        <div
          key={p.id}
          className="pb-particle"
          style={{
            width: p.size,
            height: p.size,
            background: p.isBlue ? color : '#0091ea',
            left: `${p.left}%`,
            top: `${p.top}%`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            opacity: 0,
          }}
        />
      ))}
    </>
  );
}

// ── Slide content renderers ───────────────────────────────────────────────────
function TagsContent({ tags, visible }) {
  return (
    <div style={{
      display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center',
      opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 1.6s',
    }}>
      {tags.map((tag, i) => {
        const s = TAG_STYLES[tag.color];
        return (
          <span key={i} style={{
            padding: '7px 18px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.4px',
            background: s.bg,
            color: s.color,
            border: `1px solid ${s.border}`,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {tag.label}
          </span>
        );
      })}
    </div>
  );
}

function FeaturesContent({ features, visible }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
      maxWidth: 580, margin: '24px auto 0',
      opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 1.5s',
    }}>
      {features.map((f, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00c9a7', marginTop: 5, flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.55, fontFamily: "'DM Sans', sans-serif" }}>{f}</span>
        </div>
      ))}
    </div>
  );
}

function PersonasContent({ personas, visible }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16,
      maxWidth: 760, margin: '28px auto 0',
      opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 1.5s',
    }}>
      {personas.map((p, i) => (
        <div key={i} className="pb-pcard" style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.09)',
          borderRadius: 14,
          padding: '22px 18px',
          transition: 'transform 0.25s, border-color 0.25s',
        }}>
          <div style={{ fontSize: 26, marginBottom: 12 }}>{p.icon}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>{p.title}</div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>{p.text}</div>
        </div>
      ))}
    </div>
  );
}

function StagesContent({ visible }) {
  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 0,
        maxWidth: 680, margin: '28px auto 0',
        opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 1.4s',
      }}>
        {STAGES.map((s, i) => (
          <React.Fragment key={i}>
            <div className="pb-stage-box" style={{
              flex: 1, padding: '22px 14px', textAlign: 'center',
              background: s.bg, border: `1px solid ${s.border}`, borderRadius: 12,
              transition: 'transform 0.2s',
            }}>
              <div style={{ fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', color: s.color, marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>{s.label}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0', marginBottom: 6, fontFamily: "'DM Sans', sans-serif" }}>{s.title}</div>
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, fontFamily: "'DM Sans', sans-serif" }}>{s.desc}</div>
            </div>
            {i < STAGES.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', color: '#334155', fontSize: 20 }}>›</div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div style={{
        display: 'flex', gap: 48, justifyContent: 'center', marginTop: 32,
        opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 1.8s',
      }}>
        {STATS.map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(40px, 7vw, 64px)',
              fontWeight: 900, color: '#00c9a7', lineHeight: 1,
            }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', letterSpacing: '1px', marginTop: 6, fontFamily: "'DM Sans', sans-serif" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </>
  );
}

function QuoteContent({ quote, quoteAuthor, visible }) {
  return (
    <div style={{
      maxWidth: 620, margin: '28px auto 0',
      opacity: visible ? 1 : 0, transition: 'opacity 0.7s ease 1.4s',
    }}>
      <div style={{
        borderLeft: '3px solid #00c9a7',
        padding: '24px 28px',
        background: 'rgba(0,201,167,0.04)',
        borderRadius: '0 12px 12px 0',
      }}>
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(16px, 2.5vw, 22px)',
          fontStyle: 'italic',
          color: '#cbd5e1',
          lineHeight: 1.7,
          marginBottom: 16,
        }}>{quote}</p>
        <p style={{ fontSize: 12, color: '#475569', fontFamily: "'DM Sans', sans-serif" }}>{quoteAuthor}</p>
      </div>
    </div>
  );
}

function CTAContent({ visible, navigate }) {
  return (
    <div style={{
      display: 'flex', gap: 16, justifyContent: 'center', marginTop: 36,
      flexWrap: 'wrap',
      opacity: visible ? 1 : 0, transition: 'opacity 0.6s ease 1.6s',
    }}>
      <button
        className="pb-btn-primary"
        onClick={() => navigate('/signup')}
        style={{
          padding: '16px 36px',
          background: '#00c9a7', color: '#050a0f',
          border: 'none', borderRadius: 10,
          fontSize: 15, fontWeight: 700,
          cursor: 'pointer', letterSpacing: '0.3px',
          transition: 'transform 0.2s, box-shadow 0.2s',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        Start for free →
      </button>
      <button
        className="pb-btn-ghost"
        onClick={() => navigate('/pre-production')}
        style={{
          padding: '16px 36px',
          background: 'transparent', color: '#94a3b8',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
          fontSize: 15, cursor: 'pointer',
          transition: 'border-color 0.2s, color 0.2s',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        Explore Pre-Production
      </button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const FilmProductionGuide = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => { injectStyles(); }, []);

  const goTo = useCallback((n) => {
    const next = ((n % SLIDES.length) + SLIDES.length) % SLIDES.length;
    setContentVisible(false);
    setTimeout(() => {
      setCurrent(next);
      setProgressKey(k => k + 1);
      setContentVisible(true);
    }, 300);
  }, []);

  useEffect(() => {
    setContentVisible(true);
  }, []);

  useEffect(() => {
    if (paused) { clearTimeout(timerRef.current); return; }
    timerRef.current = setTimeout(() => goTo(current + 1), DURATION);
    return () => clearTimeout(timerRef.current);
  }, [current, paused, goTo]);

  const slide = SLIDES[current];

  return (
    <div
      className="pb-guide-root"
      style={{
        minHeight: '100vh',
        background: '#050a0f',
        color: '#e8edf2',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SEO
        title="Film Production Guide"
        description="Comprehensive guides for every phase of film production — pre-production, principal photography, and post. Written for directors, producers, and crew worldwide."
        url="https://producerbuddy.pilomation.com/film-production-guide"
      />
      <Nav />

      {/* Progress bar */}
      {!paused && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 200, background: 'rgba(255,255,255,0.08)' }}>
          <div
            key={progressKey}
            className="pb-progress-bar"
            style={{
              height: '100%',
              background: `linear-gradient(90deg, ${slide.accentColor}, #0091ea)`,
              animationDuration: `${DURATION}ms`,
              animationTimingFunction: 'linear',
              animationFillMode: 'both',
            }}
          />
        </div>
      )}

      {/* Main slide area */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

        {/* Animated background */}
        <div style={{ position: 'absolute', inset: 0, background: slide.bg, transition: 'background 0.8s ease', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
          <Particles color={slide.accentColor} />
        </div>

        {/* Slide content */}
        <div
          className={contentVisible ? 'pb-slide-enter' : ''}
          style={{
            position: 'relative', zIndex: 2,
            maxWidth: 860, width: '100%',
            padding: '60px 40px', textAlign: 'center',
            opacity: contentVisible ? 1 : 0,
          }}
        >
          {/* Kicker */}
          <div style={{
            fontSize: 11, letterSpacing: '3px', textTransform: 'uppercase',
            color: slide.accentColor, marginBottom: 20,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
            opacity: contentVisible ? 1 : 0, transition: 'opacity 0.5s ease 0.2s',
          }}>
            {slide.kicker}
          </div>

          {/* Typewriter headline */}
          <Headline text={slide.headline} active={contentVisible} accentColor={slide.accentColor} />

          {/* Body */}
          {slide.body && (
            <p style={{
              fontSize: 16, lineHeight: 1.8, color: '#94a3b8',
              maxWidth: 620, margin: '0 auto 28px',
              fontFamily: "'DM Sans', sans-serif",
              opacity: contentVisible ? 1 : 0, transition: 'opacity 0.6s ease 1.2s',
            }}>
              {slide.body}
            </p>
          )}

          {/* Dynamic content by type */}
          {slide.type === 'tags' && <TagsContent tags={slide.tags} visible={contentVisible} />}
          {slide.type === 'features' && <FeaturesContent features={slide.features} visible={contentVisible} />}
          {slide.type === 'personas' && <PersonasContent personas={slide.personas} visible={contentVisible} />}
          {slide.type === 'stages' && <StagesContent visible={contentVisible} />}
          {slide.type === 'quote' && <QuoteContent quote={slide.quote} quoteAuthor={slide.quoteAuthor} visible={contentVisible} />}
          {slide.type === 'cta' && <CTAContent visible={contentVisible} navigate={navigate} />}
        </div>
      </div>

      {/* Nav dots + controls */}
      <div style={{ padding: '20px 0 16px', background: 'rgba(5,10,15,0.9)', backdropFilter: 'blur(10px)', zIndex: 10 }}>
        {/* Dots */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 14 }}>
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={i === current ? 'pb-dot-active' : ''}
              onClick={() => goTo(i)}
              style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                cursor: 'pointer', transition: 'all 0.3s',
              }}
            />
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center' }}>
          <button
            className="pb-ctrl"
            onClick={() => goTo(current - 1)}
            style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#94a3b8', padding: '8px 20px', borderRadius: 6,
              fontSize: 12, cursor: 'pointer', transition: 'all 0.2s',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            ← prev
          </button>

          <button
            className="pb-ctrl"
            onClick={() => setPaused(p => !p)}
            style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#94a3b8', padding: '8px 20px', borderRadius: 6,
              fontSize: 12, cursor: 'pointer', transition: 'all 0.2s',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {paused ? '▶ play' : '⏸ pause'}
          </button>

          <button
            className="pb-ctrl"
            onClick={() => goTo(current + 1)}
            style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#94a3b8', padding: '8px 20px', borderRadius: 6,
              fontSize: 12, cursor: 'pointer', transition: 'all 0.2s',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            next →
          </button>
        </div>

        {/* Slide indicator */}
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: '#334155', fontFamily: "'DM Sans', sans-serif", letterSpacing: '1px' }}>
          {current + 1} / {SLIDES.length} — {slide.kicker}
        </div>
      </div>
    </div>
  );
};

export default FilmProductionGuide;
